import axiosInstance from "./axiosInstance";

export interface EmployeeApi {
  id: number;
  employeeNo: string;
  itsNo: string;
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  fullName: string;
  arabicName: string;
  isNonIts: boolean;
  dateOfBirth: string;
  gender: string;
  dateOfJoining: string;
  status: string;
  hrType: string;
  roomNo: string;
  jamaaFariqYear: number;
  studiedInJamaaYear: number;
  referredBy: string;
  referrerContact: string;
  mobileNo: string;
  alternateMobile: string;
  residenceNo: string;
  whatsApp: string;
  email: string;
  alternateEmail: string;
  officeName: string;
  officeNo: string;
  mailingAddress: string;
  permanentAddress: string;
  sameAsBilling: boolean;
  picturePath: string;
}

export interface CreateEmployeePayload {
  employeeNo: string;
  itsNo: string;
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  fullName: string;
  arabicName: string;
  isNonIts: boolean;
  dateOfBirth: string;
  gender: string;
  dateOfJoining: string;
  status: string;
  hrType: string;
  roomNo: string;
  jamaaFariqYear: number;
  studiedInJamaaYear: number;
  referredBy: string;
  referrerContact: string;
  mobileNo: string;
  alternateMobile: string;
  residenceNo: string;
  whatsApp: string;
  email: string;
  alternateEmail: string;
  officeName: string;
  officeNo: string;
  mailingAddress: string;
  permanentAddress: string;
  sameAsBilling: boolean;
  picture?: File | null;
  documents?: {
    documentName: string;
    file: File;
  }[];
}

export interface EmployeeListResponse {
  items: EmployeeApi[];
  pageNo: number;
  pageSize: number;
  totalCount: number;
}

export interface UpdateEmployeePayload extends CreateEmployeePayload {
  id: number;
  picturePath?: string;
  documents?: {
    documentName: string;
    file: File;
  }[];
}

export interface EmployeePassportApi {
  id: number;
  employeeId: number;
  passportNameEn: string;
  passportNameAr: string;
  passportNo: string;
  issueDate: string;
  expiryDate: string;
}

export interface CreateEmployeePassportPayload {
  employeeId: number;
  passportNameEn: string;
  passportNameAr: string;
  passportNo: string;
  issueDate: string | null;
  expiryDate: string | null;
}

export interface EmployeeVisaApi {
  id: number;
  employeeId: number;
  visaType: string;
  visaNumber: string;
  visaFolderNo: string;
  issuedFrom: string;
  issuedDate: string;
  validity: string;
}

export interface CreateEmployeeVisaPayload {
  employeeId: number;
  visaType: string;
  visaNumber: string;
  visaFolderNo: string;
  issuedFrom: string;
  issuedDate: string | null;
  validity: string;
}

export interface EmployeeFamilyApi {
  id: number;
  employeeId: number;
  relation: string;
  itsId: string;
  name: string;
  dateOfBirth: string;
  occupation: string;
  stayingCity: string;
}

export interface CreateEmployeeFamilyPayload {
  employeeId: number;
  relation: string;
  itsId: string;
  name: string;
  dateOfBirth: string;
  occupation: string;
  stayingCity: string;
}

export interface VenueApi {
  venueId: number;
  venueName: string;
  contactNo: string;
  emailAddress: string;
  description: string;
  timeZone: string;
  isActive: boolean;
}

export interface DepartmentMasterApi {
  id: number;
  publicId: string;
  departmentCode: string;
  departmentName: string;
  status: boolean;
  description: string;
  headOfDepartment: string;
}

export interface DesignationMasterApi {
  id: number;
  publicId: string;
  designationCode: string;
  designationName: string;
  status: boolean;
}

export interface EmployeeDropdownApi {
  id: number;
  fullName: string;
}

export interface CreateEmployeeAssignmentPayload {
  employeeId: number;
  venueId: number;
  departmentId: number;
  designationId: number;
  reportingManagerId?: number | null;
  subordinateReportingManagerId?: number | null;
}

export interface EmployeeAssignmentApi {
  id: number;
  employeeId: number;
  venueId: number;
  departmentId: number;
  designationId: number;
  reportingManagerId?: number;
  subordinateReportingManagerId?: number;
  venueName: string;
  departmentName: string;
  designationName: string;
  reportingManagerName: string;
  subordinateReportingManagerName: string;
}

export interface SalaryHeadApi {
  id: number;
  publicId: string;
  salaryHeadName: string;
  code: string;
  type: "Earning" | "Deduction";
  calculationType: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface CurrencyApi {
  currencyId: number;
  currencyCode: string;
  currencyName: string;
  currencySymbol: string;
  exchangeRate: number;
  buyRate: number | null;
  sellRate: number | null;
  isBaseCurrency: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeSalaryApi {
  id: number;
  employeeId: number;
  salaryHeadId: number;
  salaryHeadName: string;
  currencyHeadId: number;
  currencyHeadName: string;
  amount: number;
  remuneration: string;
  withEffectFrom: string;
  isActive: boolean;
}

export interface CreateEmployeeSalaryPayload {
  employeeId: number;
  salaryHeadId: number;
  currencyHeadId: number;
  amount: number;
  remuneration: string;
  withEffectFrom: string;
  isActive: boolean;
}

export interface UpdateEmployeeSalaryPayload {
  id: number;
  employeeId: number;
  salaryHeadId: number;
  currencyHeadId: number;
  amount: number;
  remuneration: string;
  withEffectFrom: string;
  isActive: boolean;
}

export interface QarzanTypeApi {
  id: number;
  qarzanCode: string;
  qarzanTypeName: string;
  isActive: boolean;
}

export interface EmployeeQarzanApi {
  id: number;
  employeeId: number;
  qarzanTypeId: number;
  amount: number;
  issuedDate: string;
  dueDate: string;
  remaining: number;
  isActive: boolean;
}

export interface CreateEmployeeQarzanPayload {
  employeeId: number;
  qarzanTypeId: number;
  amount: number;
  issuedDate: string;
  dueDate: string;
  remaining: number;
  isActive: boolean;
}

export interface EmployeeWithDocumentsResponse {
  employee: EmployeeApi;

  documents: {
    documentId: number;
    documentMasterId: number;
    documentName: string;
    acceptedFormats: string;
    isMandatory: boolean;
    maxFileSizeKB: number;

    versions: {
      documentVersionId: number;
      fileName: string;
      filePath: string;
      documentVersionStatus: string;
      documentVersionRemarks: string | null;

      histories: {
        approvalHistoryId: number;
        historyStatus: string;
        remarks: string | null;
        actionBy: number;
        actionAt: string;
      }[];
    }[];
  }[];
}

export interface UploadEmployeeDocumentPayload {
  employeeId: number;
  documentMasterId: number;
  file: File;
}

export const getAuthHeader = () => {
  const token = localStorage.getItem("fhims_auth_token");

  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getEmployees = async (params: {
  searchText?: string | null;
  isActive?: boolean;
  departmentId?: number;
  venueId?: number;
  status?: string;
  hrType?: string;
  pageNo: number;
  pageSize: number;
}): Promise<EmployeeListResponse> => {
  const res = await axiosInstance.get("/EmployeeMaster/list", {
    headers: getAuthHeader(),
    params,
  });

  return res.data;
};

export const getEmployeeById = async (
  employeeNo: string,
): Promise<EmployeeApi> => {
  const res = await axiosInstance.get(`/EmployeeMaster/${employeeNo}`, {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getEmployeeWithDocuments = async (
  id: number,
): Promise<EmployeeWithDocumentsResponse> => {
  const res = await axiosInstance.get(`/EmployeeMaster/${id}/with-documents`, {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const uploadEmployeeDocument = async (
  payload: UploadEmployeeDocumentPayload,
) => {
  const formData = new FormData();

  formData.append("EmployeeId", String(payload.employeeId));

  formData.append("DocumentMasterId", String(payload.documentMasterId));

  formData.append("File", payload.file);

  const res = await axiosInstance.post(
    "/EmployeeMaster/document/upload",
    formData,
    {
      headers: {
        ...getAuthHeader(),
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return res.data;
};

export const rejectEmployeeDocument = async (
  documentId: number,
  remarks: string,
) => {
  const res = await axiosInstance.post(
    `/documents-central/${documentId}/reject`,
    remarks,
    {
      headers: {
        ...getAuthHeader(),
        "Content-Type": "application/json",
      },
    },
  );

  return res.data;
};

export const createEmployee = async (payload: CreateEmployeePayload) => {
  const formData = new FormData();

  formData.append("EmployeeNo", payload.employeeNo);
  formData.append("ITSNo", payload.itsNo);
  formData.append("Title", payload.title);
  formData.append("FirstName", payload.firstName);
  formData.append("MiddleName", payload.middleName || "");
  formData.append("LastName", payload.lastName);
  formData.append("FullName", payload.fullName);
  formData.append("ArabicName", payload.arabicName);
  formData.append("IsNonIts", String(payload.isNonIts));
  formData.append("DateOfBirth", payload.dateOfBirth);
  formData.append("Gender", payload.gender);
  formData.append("DateOfJoining", payload.dateOfJoining);
  formData.append("Status", payload.status);
  formData.append("HRType", payload.hrType);
  formData.append("RoomNo", payload.roomNo);
  formData.append("JamaaFariqYear", String(payload.jamaaFariqYear));
  formData.append("StudiedInJamaaYear", String(payload.studiedInJamaaYear));
  formData.append("ReferredBy", payload.referredBy);
  formData.append("ReferrerContact", payload.referrerContact);
  formData.append("MobileNo", payload.mobileNo);
  formData.append("AlternateMobile", payload.alternateMobile);
  formData.append("ResidenceNo", payload.residenceNo);
  formData.append("WhatsApp", payload.whatsApp);
  formData.append("Email", payload.email);
  formData.append("AlternateEmail", payload.alternateEmail);
  formData.append("OfficeName", payload.officeName);
  formData.append("OfficeNo", payload.officeNo);
  formData.append("MailingAddress", payload.mailingAddress);
  formData.append("PermanentAddress", payload.permanentAddress);
  formData.append("SameAsBilling", String(payload.sameAsBilling));

  if (payload.picture) {
    formData.append("picture", payload.picture);
  }

  payload.documents?.forEach((doc, index) => {
    formData.append(`Documents[${index}].DocumentName`, doc.documentName);

    formData.append(`Documents[${index}].File`, doc.file);
  });

  const res = await axiosInstance.post("/EmployeeMaster", formData, {
    headers: {
      ...getAuthHeader(),
      "Content-Type": "multipart/form-data",
    },
  });

  return res.data;
};

export const updateEmployee = async (payload: UpdateEmployeePayload) => {
  const formData = new FormData();

  formData.append("Id", String(payload.id));

  formData.append("EmployeeNo", payload.employeeNo);
  formData.append("ITSNo", payload.itsNo);
  formData.append("Title", payload.title);
  formData.append("FirstName", payload.firstName);
  formData.append("MiddleName", payload.middleName || "");
  formData.append("LastName", payload.lastName);
  formData.append("FullName", payload.fullName);
  formData.append("ArabicName", payload.arabicName);
  formData.append("IsNonIts", String(payload.isNonIts));
  formData.append("DateOfBirth", payload.dateOfBirth);
  formData.append("Gender", payload.gender);
  formData.append("DateOfJoining", payload.dateOfJoining);
  formData.append("Status", payload.status);
  formData.append("HRType", payload.hrType);
  formData.append("RoomNo", payload.roomNo);
  formData.append("JamaaFariqYear", String(payload.jamaaFariqYear));
  formData.append("StudiedInJamaaYear", String(payload.studiedInJamaaYear));
  formData.append("ReferredBy", payload.referredBy);
  formData.append("ReferrerContact", payload.referrerContact);
  formData.append("MobileNo", payload.mobileNo);
  formData.append("AlternateMobile", payload.alternateMobile);
  formData.append("ResidenceNo", payload.residenceNo);
  formData.append("WhatsApp", payload.whatsApp);
  formData.append("Email", payload.email);
  formData.append("AlternateEmail", payload.alternateEmail);
  formData.append("OfficeName", payload.officeName);
  formData.append("OfficeNo", payload.officeNo);
  formData.append("MailingAddress", payload.mailingAddress);
  formData.append("PermanentAddress", payload.permanentAddress);
  formData.append("SameAsBilling", String(payload.sameAsBilling));
  formData.append("PicturePath", payload.picturePath || "");

  if (payload.picture) {
    formData.append("picture", payload.picture);
  }

  payload.documents?.forEach((doc, index) => {
    formData.append(`Documents[${index}].DocumentName`, doc.documentName);

    formData.append(`Documents[${index}].File`, doc.file);
  });

  const res = await axiosInstance.put("/EmployeeMaster", formData, {
    headers: {
      ...getAuthHeader(),
      "Content-Type": "multipart/form-data",
    },
  });

  return res.data;
};

export const getEmployeePassportDetails = async (
  employeeId: number,
): Promise<EmployeePassportApi[]> => {
  const res = await axiosInstance.get(
    `/EmployeePassport/employee/${employeeId}/all`,
    {
      headers: getAuthHeader(),
    },
  );

  return res.data;
};

export const getEmployeeVisaDetails = async (
  employeeId: number,
): Promise<EmployeeVisaApi[]> => {
  const res = await axiosInstance.get(
    `/EmployeeVisa/employee/${employeeId}/all`,
    {
      headers: getAuthHeader(),
    },
  );

  return res.data;
};

export const createEmployeePassport = async (
  payload: CreateEmployeePassportPayload,
): Promise<EmployeePassportApi> => {
  const res = await axiosInstance.post("/EmployeePassport", payload, {
    headers: {
      ...getAuthHeader(),
      "Content-Type": "application/json",
    },
  });

  return res.data;
};

export const createEmployeeVisa = async (
  payload: CreateEmployeeVisaPayload,
): Promise<EmployeeVisaApi> => {
  const res = await axiosInstance.post("/EmployeeVisa", payload, {
    headers: {
      ...getAuthHeader(),
      "Content-Type": "application/json",
    },
  });

  return res.data;
};

export const getEmployeeFamilyDetails = async (
  employeeId: number,
): Promise<EmployeeFamilyApi[]> => {
  const res = await axiosInstance.get(
    `/EmployeeFamily/employee/${employeeId}`,
    {
      headers: getAuthHeader(),
    },
  );

  return Array.isArray(res.data) ? res.data : [res.data];
};

export const createEmployeeFamily = async (
  payload: CreateEmployeeFamilyPayload,
) => {
  const res = await axiosInstance.post("/EmployeeFamily", payload, {
    headers: {
      ...getAuthHeader(),
      "Content-Type": "application/json",
    },
  });

  return res.data;
};

export const deleteEmployeeFamily = async (id: number): Promise<void> => {
  await axiosInstance.delete(`/EmployeeFamily/${id}`, {
    headers: getAuthHeader(),
  });
};

export const getVenues = async (): Promise<VenueApi[]> => {
  const res = await axiosInstance.get("/Venues?onlyActive=true", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getDepartments = async (): Promise<DepartmentMasterApi[]> => {
  const res = await axiosInstance.get("/department-master", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getDesignations = async (): Promise<DesignationMasterApi[]> => {
  const res = await axiosInstance.get("/designation-master", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getEmployeesDropdown = async (): Promise<
  EmployeeDropdownApi[]
> => {
  const res = await axiosInstance.get("/EmployeeMaster", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getEmployeeAssignment = async (
  employeeId: number,
): Promise<EmployeeAssignmentApi[]> => {
  const res = await axiosInstance.get(
    `/EmployeeAssignment/employee/${employeeId}`,
    {
      headers: getAuthHeader(),
    },
  );

  return Array.isArray(res.data) ? res.data : [res.data];
};

export const createEmployeeAssignment = async (
  payload: CreateEmployeeAssignmentPayload,
): Promise<EmployeeAssignmentApi> => {
  const res = await axiosInstance.post("/EmployeeAssignment", payload, {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const deleteEmployeeAssignment = async (id: number) => {
  await axiosInstance.delete(`/EmployeeAssignment/${id}`, {
    headers: getAuthHeader(),
  });
};

export const getSalaryHeads = async (): Promise<SalaryHeadApi[]> => {
  const res = await axiosInstance.get("/salary-head-master", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getCurrencies = async (): Promise<CurrencyApi[]> => {
  const res = await axiosInstance.get("/Currency", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getEmployeeSalary = async (
  employeeId: number,
): Promise<EmployeeSalaryApi[]> => {
  const res = await axiosInstance.get(
    `/EmployeeSalary/employee/${employeeId}`,
    {
      headers: getAuthHeader(),
    },
  );

  return res.data;
};

export const createEmployeeSalary = async (
  payload: CreateEmployeeSalaryPayload,
) => {
  const res = await axiosInstance.post("/EmployeeSalary", payload, {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const updateEmployeeSalary = async (
  payload: UpdateEmployeeSalaryPayload,
): Promise<EmployeeSalaryApi> => {
  const res = await axiosInstance.put("/EmployeeSalary", payload, {
    headers: {
      ...getAuthHeader(),
      "Content-Type": "application/json",
    },
  });

  return res.data;
};

export const deleteEmployeeSalary = async (id: number): Promise<void> => {
  await axiosInstance.delete(`/EmployeeSalary/${id}`, {
    headers: {
      ...getAuthHeader(),
    },
  });
};

export const getQarzanTypes = async (): Promise<QarzanTypeApi[]> => {
  const res = await axiosInstance.get("/qarzan-type", {
    headers: getAuthHeader(),
  });
  return res.data;
};

export const getEmployeeQarzan = async (
  employeeId: number,
): Promise<EmployeeQarzanApi[]> => {
  const res = await axiosInstance.get(
    `/EmployeeQarzan/employee/${employeeId}/all`,
    {
      headers: getAuthHeader(),
    },
  );

  return res.data;
};

export const createEmployeeQarzan = async (
  payload: CreateEmployeeQarzanPayload,
): Promise<EmployeeQarzanApi> => {
  const res = await axiosInstance.post("/EmployeeQarzan", payload, {
    headers: {
      ...getAuthHeader(),
      "Content-Type": "application/json",
    },
  });

  return res.data;
};
